import React, { useMemo, useCallback } from 'react';
import { SocialConsentModal, SocialConsentModalProps } from '@minecraft.organisms';
import { FEATURE_LIST_ITEMS, FOOTER_ITEMS } from '@blocs.social';
import { appConfig as globalAppConfig } from '@minecraft.app-config';
import { PaywallLocation, useBillingPopup } from '@blocs.paywall';
import { useTranslation } from '@blocs.i18n';

interface SocialPaywallConsentModalProps {
  isSocialPaywallConsentModalOpen: boolean;
  closeSocialPaywallConsentModal: () => void;
}

export const SocialPaywallConsentModal = ({
  closeSocialPaywallConsentModal,
  isSocialPaywallConsentModalOpen,
}: SocialPaywallConsentModalProps) => {
  const { t } = useTranslation();
  const billingPopup = useBillingPopup();

  const openBilling = useCallback(() => {
    billingPopup.show({
      location: PaywallLocation.SOCIAL_UPGRADE,
    });
  }, [billingPopup]);

  const modalButtons: SocialConsentModalProps['buttonsConfig'] = useMemo(
    () => [
      {
        label: t('common:button.notNow'),
        onClick: closeSocialPaywallConsentModal,
        variant: 'secondary',
      },
      {
        onClick: openBilling,
        startIcon: { name: 'rocket', justify: 'end' },
        endIcon: undefined,
        fixedWidth: true,
        label: { name: t('common:membership.upgradeToPremiumShort'), justify: 'start' },
        color: 'hotTrialPaywall',
      },
    ],
    [t, closeSocialPaywallConsentModal, openBilling]
  );

  const { featureItems, footerItems } = useMemo(
    () => ({ featureItems: FEATURE_LIST_ITEMS(), footerItems: FOOTER_ITEMS(t) }),
    [t]
  );
  return (
    <SocialConsentModal
      isOpen={isSocialPaywallConsentModalOpen}
      close={closeSocialPaywallConsentModal}
      description={t('common:profileEdit.social.consentModal.description')}
      platformIcon="profile-social-instagram"
      previewImage={{
        src: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview.png`,
        srcSet: `${globalAppConfig.cdnRoot}/images/static/profile-social-preview-4x.png`,
        alt: t('common:profileEdit.social.consentModal.previewImageAlt'),
      }}
      title={t('common:profileEdit.social.instagram.consentModal.title')}
      data-testid="instagram-auth-modal"
      buttonsConfig={modalButtons}
      footerItems={footerItems}
      featureHeader={t('common:profileEdit.social.consentModal.featureHeader')}
      features={featureItems}
    />
  );
};
